"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import Link from "next/link";
import { Gamja_Flower } from "next/font/google";
import { Sparkles, ArrowRight, Flame, TrendingUp } from "lucide-react";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import Banner from "@/components/Banner";
import HomeNoticePopups from "@/components/HomeNoticePopups";
import HomeHero from "@/components/home/HomeHero";
import DdayCard from "@/components/home/DdayCard";
import ContinueLearningCard from "@/components/home/ContinueLearningCard";
import ExamReviewCard from "@/components/home/ExamReviewCard";
import PracticeQuizBanners from "@/components/home/PracticeQuizBanners";
import HomeHelperSections from "@/components/home/HomeHelperSections";
import SidebarSectionLabel from "@/components/home/SidebarSectionLabel";

const gamja = Gamja_Flower({
  weight: "400",
  subsets: ["latin"],
  display: "swap",
  fallback: ["serif"],
});

/**
 * 홈(`/`) 대시보드 콘텐츠 — "감자의 합격 책상" (JCG-2110).
 *
 * 데스크탑(≥1024px)에서는 ArticleShell 의 custom 셸(`variant="custom"`) 안에서 렌더되며,
 * 좌/우 사이드바를 ArticleShellContext 의 custom 슬롯에 createPortal 로 그린다(JCG-2073 /my,
 * JCG-2104 /forum/jcg 와 동일 [D] portal 패턴):
 * - 좌(오늘의 나): D-day 카드 + 오늘의 감자시험 + 내 공부 이어가기
 * - 중앙(합격 여정): 환영 히어로 + 과목 탐색 허브 + 기출 모아보기
 * - 우(감자 소식): 추천 글 + 합격 도우미 배너 + 최근 회차 합격률
 *
 * 모바일·태블릿(<1024px, inShell=false)에서는 세로 랜딩으로 폴백한다.
 * 슬롯은 셸 마운트 후 ref 콜백으로 채워지므로 `slot && createPortal(...)` null 가드 필수.
 *
 * async 서버 컴포넌트(SubjectBrowser/ExamButtons/RecommendedPosts)는 page.tsx(서버)가 렌더해
 * ReactNode prop 으로 주입한다. 폭이 다른 위젯은 full/sidebar 두 변형을 함께 받는다.
 */
interface HomeDashboardProps {
  /** 과목 탐색(SubjectBrowser) — 중앙 본문(셸·클래식 공통). */
  subjectBrowser: React.ReactNode;
  /** 오늘의 감자시험 — 넓은 섹션(클래식/모바일). */
  dailyExamFull: React.ReactNode;
  /** 오늘의 감자시험 — 토이 타일만(셸 좌측 카드 안). */
  dailyExamTiles: React.ReactNode;
  /** 감자 추천 글 — 넓은 Table(클래식/모바일). */
  recommendedPostsFull: React.ReactNode;
  /** 감자 추천 글 — 컴팩트 목록(셸 우측). */
  recommendedPostsSidebar: React.ReactNode;
}

export default function HomeDashboard({
  subjectBrowser,
  dailyExamFull,
  dailyExamTiles,
  recommendedPostsFull,
  recommendedPostsSidebar,
}: HomeDashboardProps) {
  const { inShell, customLeftSlot, customRightSlot } = useArticleShellSlots();

  // 기출 모아보기 CTA 배너(와이드) — 모바일·클래식 세로 랜딩 전용.
  // 데스크탑 셸에서는 좌측 사이드바 컴팩트 카드(pastExamSidebar)로 대체한다.
  const pastExamCta = (
    <Link
      href="/past-exam"
      className="group relative my-8 block overflow-hidden rounded-3xl border border-orange-200 bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 p-5 shadow-sm transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 sm:p-6 dark:border-orange-900/60 dark:from-orange-950/30 dark:via-amber-950/20 dark:to-orange-950/30"
    >
      <div className="flex items-center justify-between gap-4 sm:gap-5">
        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center gap-1 rounded-full bg-orange-500 px-2.5 py-0.5 text-xs font-bold text-white">
            <Sparkles className="h-3 w-3" aria-hidden="true" /> 기출 모아보기
          </span>
          <h2 className="mt-2 text-lg font-bold text-orange-900 sm:text-xl dark:text-orange-100">
            정처기 실기 기출문제
          </h2>
          <p className="mt-1 text-sm text-orange-800/80 dark:text-orange-200/80">
            2020년부터 최신 회차까지, 회차별 후기와 기출을 한눈에
          </p>
          <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-orange-700 transition-[gap] group-hover:gap-2 dark:text-orange-300">
            기출 보러가기 <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </span>
        </div>
        <Image
          src="/past-exam/past-exam-clock_gamja.webp"
          alt=""
          width={120}
          height={120}
          className="h-24 w-24 shrink-0 object-contain transition-transform group-hover:scale-105 sm:h-28 sm:w-28"
        />
      </div>
    </Link>
  );

  // ===== 모바일·태블릿(셸 밖) — 세로 랜딩 =====
  if (!inShell) {
    return (
      <main className="dark:text-white">
        <HomeNoticePopups />
        <div className="px-4 pt-4">
          <HomeHero />
        </div>
        <div className="px-4">
          <DdayCard variant="mobile" />
        </div>
        <Banner variant="mobile-home" />
        <section className="px-4 pb-3" aria-label="빠른 학습 메뉴">
          <div className="grid grid-cols-2 gap-2">
            <ExamReviewCard compact />
            <ContinueLearningCard compact />
          </div>
          <div className="mt-2">
            <PracticeQuizBanners layout="mobile-grid" />
          </div>
        </section>
        {subjectBrowser}
        <div className="px-4">{pastExamCta}</div>
        {dailyExamFull}
        {recommendedPostsFull}
      </main>
    );
  }

  // ===== 데스크탑(셸 안) — 3컬럼 대시보드 =====

  // 기출 모아보기 — 좌측 사이드바 컴팩트 카드(372px 좁은 폭 대응). 와이드 배너의 가로 구성을
  // 줄여 시계 감자 + 배지/제목을 한 줄에, 설명·CTA를 아래로 쌓는다(다른 좌측 카드와 동일 rounded-3xl).
  const pastExamSidebar = (
    <Link
      href="/past-exam"
      className="group relative block overflow-hidden rounded-3xl border border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-orange-50 p-4 shadow-sm transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 dark:border-orange-900/60 dark:from-orange-950/30 dark:via-amber-950/20 dark:to-orange-950/30"
    >
      <div aria-hidden className="absolute -right-5 -top-5 h-20 w-20 rounded-full bg-orange-200/40 blur-xl dark:bg-orange-700/20" />
      <div className="relative flex items-center gap-3">
        <Image
          src="/past-exam/past-exam-clock_gamja.webp"
          alt=""
          width={64}
          height={64}
          className="h-16 w-16 shrink-0 object-contain transition-transform group-hover:scale-105"
        />
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1 rounded-full bg-orange-500 px-2 py-0.5 text-[11px] font-bold text-white">
            <Sparkles className="h-2.5 w-2.5" aria-hidden="true" /> 기출 모아보기
          </span>
          <h2 className="mt-1.5 text-base font-bold leading-tight text-orange-900 dark:text-orange-100">
            정처기 실기 기출문제
          </h2>
        </div>
      </div>
      <p className="relative mt-3 text-xs text-orange-800/80 dark:text-orange-200/80">
        2020년부터 최신 회차까지, 회차별 후기와 기출을 한눈에
      </p>
      <span className="relative mt-2.5 inline-flex items-center gap-1 text-sm font-bold text-orange-700 transition-[gap] group-hover:gap-2 dark:text-orange-300">
        기출 보러가기 <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </span>
    </Link>
  );

  // 좌: 오늘의 나 (D-day · 오늘의 감자시험 · 이어가기 · 기출 모아보기)
  const leftWidgets = (
    <div className="space-y-4">
      <DdayCard />
      <div className="rounded-3xl border border-border bg-card/90 p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-300">
            <Flame className="h-[18px] w-[18px]" aria-hidden="true" />
          </span>
          <div>
            <h2 className={`${gamja.className} text-lg leading-none text-foreground`}>
              오늘의 감자시험
            </h2>
            <p className="mt-0.5 text-[11px] text-muted-foreground">매일 오전 4시 새 미션 🥔</p>
          </div>
        </div>
        {dailyExamTiles}
      </div>
      <PracticeQuizBanners />
      <ContinueLearningCard />
      {pastExamSidebar}
    </div>
  );

  // 우: 감자 소식 — 최근 글 · 최근 회차 합격률 · (합격 도우미 분해) 압축 PDF · 압축 공부법 · 공부계획.
  // 위젯 묶음 space-y-4 + 섹션 라벨 통일(three-column-layout.md §JCG-2110): 좌/우 동일하게
  // `mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground`.
  // HomeHelperSections 는 fragment 로 3개 섹션을 직접 자식으로 펼쳐 같은 space-y-4 간격을 공유한다.
  const rightWidgets = (
    <div className="space-y-4">
      {recommendedPostsSidebar}
      <div>
        <SidebarSectionLabel icon={TrendingUp}>최근 회차 합격률</SidebarSectionLabel>
        <ExamReviewCard />
      </div>
      <HomeHelperSections />
    </div>
  );

  return (
    <>
      <HomeNoticePopups />
      <HomeHero />
      {subjectBrowser}

      {/* 좌/우 사이드바 — 셸 custom 슬롯에 portal(슬롯 마운트 후 그려짐).
          기출 모아보기는 중앙이 아닌 좌측 사이드바 카드(pastExamSidebar)로 들어간다. */}
      {customLeftSlot && createPortal(leftWidgets, customLeftSlot)}
      {customRightSlot && createPortal(rightWidgets, customRightSlot)}
    </>
  );
}
